import {Getter} from '../getters/getter'
import {DotPath} from '../getOrElse'
import {isDate, getValueFromString} from './utils'
import {
  camelCase,
  isArray,
  flatMapDeep,
  reduce,
  isEqual
} from 'lodash'
import {
  arrayOr,
  booleanOr,
  dateOr,
  numberOr,
  stringOr,
  objectOr
} from '../getters'
import {Log} from '..'

export enum Type {
  ARRAY = 'array',
  BOOLEAN = 'boolean',
  DATE = 'date',
  NUMBER = 'number',
  OBJECT = 'object',
  STRING = 'string',
  UNDEFINED = 'undefined'
}

const typeMap = {
  [Type.ARRAY]: arrayOr,
  [Type.BOOLEAN]: booleanOr,
  [Type.DATE]: dateOr,
  [Type.NUMBER]: numberOr,
  [Type.OBJECT]: objectOr,
  [Type.STRING]: stringOr
}

function jsType(value: any): Type {
  return typeof value as Type
}

function dotCount(value: string): number {
  return (value.match(/./g) || []).length
}

export function mapType(value: any): Type {
  return isArray(value) ? Type.ARRAY : [].concat(value).map(
      val => {
        if (isDate(val))
          return Type.DATE
        return jsType(val)
      }
    ).filter(val => val !== undefined).shift()
}

/**
 * A bit ugly, but we want to find the appropriate getters for members of
 * your class.
 *
 * @param obj Some object
 * @param key A key for said object
 */
export function findAppropriateGetter(obj: any, key: string): Getter<any, any> {
  Log.info('FINDING FOR ', obj, key, ' = ', getValueFromString(obj, key))

  return typeMap[mapType(
      getValueFromString(obj, key)
    )]
}

export function appendPath(parent: string, child: string): string {
  return (parent) ? `${parent}.${child}` : child
}

export function findAllDotPaths(json: object, parent: string = ''): DotPath {
  return (json) ? flatMapDeep(Object.keys(json),
    key => {
      const currentPath = appendPath(parent, key)
      return (typeof json[key] === 'object' && !isArray(json[key])) ?
        [currentPath].concat(
          findAllDotPaths(json[key], currentPath)
        ) :
        currentPath
    }
  ) as DotPath : []
}

export function doFullPathsMatch(a: string, b: string): boolean {
  return isEqual(a.split('.').map(camelCase),
   b.split('.').map(camelCase))
}

export function doesPathMatchGreedy(a: string, b: string): boolean {
  return a.split('.').map(camelCase).pop() ===
    b.split('.').map(camelCase).pop()
}

export function doPathsMatch(target: string, source: string): boolean {
  return doFullPathsMatch(target, source) ||
    doesPathMatchGreedy(target, source)
}

export function findMatchingFields(
  key: string,
  json: object,
  ingorePaths: DotPath = ''
): DotPath {
  const ignoreable = [].concat(ingorePaths)
  return reduce(
    [].concat(findAllDotPaths(json)),
    (acc, dotPath) => {
      return !ignoreable.find(
            path => doFullPathsMatch(dotPath, path)
          ) && doPathsMatch(key, dotPath) ? acc.concat(dotPath) : acc
    },
    []
  ).sort((a, b) => dotCount(a) > dotCount(b) ? 1 : -1)
}
